// Ability roster + tuning for the HIDDEN variant (/hidden), separate from the base game so
// it can freely diverge in names, hotkeys, colors, unlock order and numbers. This starts as
// an exact copy of the base 8 (see balance.ts); edit here to change what the hidden game's
// abilities are called and how strong they are. The behavior/animation code lives in
// abilitiesHidden.ts (dispatched by each ability's `key`). Everything else (enemies, player,
// spawns, XP, weapons, difficulty) stays shared with the base game via balance.ts.

import type { AbilityDef } from './balance';

export const ABILITIES_HIDDEN: ReadonlyArray<AbilityDef> = [
  { key: 'berserk',   name: 'Berze Zeci',  unlockLevel: 5,  hotkey: 'KeyA', label: 'A', cooldownSec: 25, durationSec: 5,   color: 0xff3030, desc: '10 storks orbit you, striking nearby foes' },
  { key: 'cryo',      name: 'Cry Oh Nova', unlockLevel: 10, hotkey: 'KeyS', label: 'S', cooldownSec: 30, durationSec: 0,   color: 0x4fd6ff, desc: 'Burst of water droplets: damage + slow' },
  { key: 'transmute', name: 'Trans Mute',  unlockLevel: 15, hotkey: 'KeyD', label: 'D', cooldownSec: 35, durationSec: 0,   color: 0xff4dff, desc: 'Silence: notes burst, damaging nearby foes' },
  { key: 'aegis',     name: 'Aegis',       unlockLevel: 20, hotkey: 'KeyF', label: 'F', cooldownSec: 30, durationSec: 5,   color: 0xffd34d, desc: 'Shield + heal + knockback' },
  { key: 'blackhole', name: 'Back Hole',   unlockLevel: 25, hotkey: 'KeyZ', label: 'Z', cooldownSec: 40, durationSec: 2.2, color: 0x9b5cff, desc: 'Drop a hole behind you; it pulls + detonates' },
  { key: 'tempest',   name: 'Tâmpești',    unlockLevel: 30, hotkey: 'KeyX', label: 'X', cooldownSec: 30, durationSec: 4,   color: 0x59ffd0, desc: 'Confuse + slow all enemies; you move faster' },
  { key: 'meltdown',  name: 'Maindan',     unlockLevel: 35, hotkey: 'KeyC', label: 'C', cooldownSec: 35, durationSec: 4,   color: 0xff7a2a, desc: 'Raise a boulder ring that traps enemies' },
  { key: 'overload',  name: 'OverLord',    unlockLevel: 40, hotkey: 'KeyV', label: 'V', cooldownSec: 40, durationSec: 3,   color: 0xffe14d, desc: 'Invulnerable, double size, 10x damage' },
];

/** Per-ability tuning for the hidden variant (its own reworked abilities). */
export const ABILITY_HIDDEN = {
  // Berze Zeci: 10 storks orbit the player, each striking foes it passes over.
  storkCount: 10,
  storkRingRadius: 96, // orbit distance from the player
  storkOrbRadius: 30, // damage radius around each stork
  storkSpinSpeed: 2.4, // rad/sec
  storkDmgMul: 2, // x the strongest weapon's per-hit damage
  storkTickSec: 0.18, // how often storks deal damage

  // Cry Oh Nova: instant water burst around the player - damage + slow.
  cryoRadius: 340,
  cryoDmgMul: 3, // x the strongest weapon's per-hit damage
  cryoSlowSec: 3,
  cryoSlowMul: 0.4,

  // Trans Mute: "Silence" - a note burst that damages nearby enemies.
  transmuteRadius: 240,
  transmuteDamage: 240,

  // Aegis (unchanged): shield + heal + knockback.
  aegisHeal: 40,
  aegisKnockRadius: 175,
  aegisKnockback: 150,

  // Back Hole: dropped at cast; sits, then pulls enemies in and detonates.
  bhDropDelay: 1.0, // seconds it sits before pulling
  bhPullSec: 1.2, // seconds it pulls (duration = drop + pull = 2.2)
  bhRadius: 560, // pull range
  bhPullPerSec: 520,
  bhBoomRadius: 220,
  bhBoomDamage: 260,

  // Tâmpești: confuse + slow every enemy; small player speed boost.
  confuseSec: 4,
  confuseSlowMul: 0.5,
  tempSpeedMul: 1.1,

  // Maindan: a ring of boulders around the player that traps enemies.
  ringRadius: 150, // distance from player to the ring
  ringBoulders: 16, // enough to overlap into a solid wall
  ringBoulderRadius: 30,

  // OverLord: invulnerable, double size, 10x damage.
  overlordDmgMul: 10,
  overlordSizeMul: 2,
} as const;
